import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    ArrowLeft,
    Copy,
    Check,
    QrCode,
    Info,
    Loader2,
    CheckCircle2,
    Mail,
    Smartphone,
    ShieldCheck,
    Zap,
    ShoppingBag,
    ChevronRight,
    Lock,
    ExternalLink,
    AlertCircle,
    SmartphoneNfc,
    CreditCard,
    Sparkles
} from 'lucide-react';
import QRCode from 'react-qr-code';

const API_BASE = "https://eventbooking.apanel.leewaysofttech.com/BOOK_YOUR_SHOW_API";
const MERCHANT_UPI_ID = "9499505454@okbizaxis";
const PAYEE_NAME = "ROTARACT CLUB OF BHAVNAGAR YOUTH";
const RAZORPAY_KEY = "rzp_test_nhFQ8SjDsXZXZJ";

const PaymentScreen = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    
    const [utr, setUtr] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [loading, setLoading] = useState(false);
    const [isTestMode, setIsTestMode] = useState(false);
    const [copied, setCopied] = useState(false);
    const [touched, setTouched] = useState({});

    useEffect(() => {
        if (!state || !state.event || !state.selectedSeats) {
            navigate('/');
        }
    }, [state, navigate]);

    if (!state) return null;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isDetailsValid = emailRegex.test(email) && phone.length === 10;
    const finalAmount = isTestMode ? 1 : state.totalAmount;

    // --- TRANSACTION NOTE FIX ---
    const getSafeTransactionNote = () => {
        const title = state.event.title || "";
        let clean = title.replace(/[^\x00-\x7F]/g, "").trim();
        if (!clean) {
            if (title.includes("શ્યામ") || title.includes("શબદ")) clean = "Shyam_Shabd";
            else clean = "Event_Pass";
        }
        return `Booking_${state.event.id}_${clean.replace(/\s+/g, "_")}`.substring(0, 25);
    };

    const upiLink = `upi://pay?pa=${MERCHANT_UPI_ID}&pn=${encodeURIComponent(PAYEE_NAME)}&am=${finalAmount}&cu=INR&tn=${encodeURIComponent(getSafeTransactionNote())}`;

    // 1. RAZORPAY FLOW
    const handleRazorpayPayment = async () => {
        if (!isDetailsValid) {
            setTouched({ email: true, phone: true });
            document.getElementById('contact-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/create_order.php`, {
                method: 'POST',
                body: JSON.stringify({ amount: finalAmount }),
                headers: { 'Content-Type': 'application/json' }
            });
            const order = await res.json();

            const options = {
                key: RAZORPAY_KEY,
                amount: order.amount,
                currency: "INR",
                name: "ROTARACT CLUB OF BHAVNAGAR YOUTH",
                description: `Booking for ${state.event.title}`,
                order_id: order.id,
                handler: async (response) => {
                    setLoading(true);
                    const verify = await fetch(`${API_BASE}/verify_payment.php`, {
                        method: 'POST',
                        body: JSON.stringify({
                            ...response,
                            event_id: state.event.id,
                            email,
                            phone,
                            seats: state.selectedSeats.join(', '),
                            total_amount: finalAmount
                        }),
                        headers: { 'Content-Type': 'application/json' }
                    });
                    const result = await verify.json();
                    if (result.success) {
                        navigate('/ticket', {
                            state: {
                                bookingData: {
                                    eventTitle: state.event.title,
                                    eventVenue: state.event.location,
                                    eventDateTime: `${state.event.date} | ${state.event.start_time || state.event.time}`,
                                    selectedSeats: state.selectedSeats,
                                    txnId: response.razorpay_payment_id,
                                    orderId: result.order_id,
                                    totalAmount: finalAmount,
                                    phone,
                                    email
                                }
                            }
                        });
                    }
                },
                prefill: { email, contact: phone },
                theme: { color: "#C5A059" },
                modal: { ondismiss: () => setLoading(false) }
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
        } catch (err) {
            console.error(err);
            alert("Error launching gateway");
            setLoading(false);
        }
    };

    // 2. MANUAL FALLBACK FLOW
    const handleManualVerify = async () => {
        if (utr.length < 12) {
            alert("Please enter the 12-digit UTR number from your payment receipt.");
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/book_event.php`, {
                method: 'POST',
                body: JSON.stringify({
                    event_id: state.event.id,
                    email,
                    phone,
                    seats: state.selectedSeats.join(', '),
                    total_amount: finalAmount,
                    payment_status: 'SUCCESS',
                    payment_id: utr
                }),
                headers: { 'Content-Type': 'application/json' }
            });
            const result = await res.json();
            if (result.success) {
                navigate('/ticket', {
                    state: {
                        bookingData: {
                            eventTitle: state.event.title,
                            eventVenue: state.event.location,
                            eventDateTime: `${state.event.date} | ${state.event.start_time || state.event.time}`,
                            selectedSeats: state.selectedSeats,
                            txnId: utr,
                            orderId: result.order_id,
                            totalAmount: finalAmount,
                            phone,
                            email
                        }
                    }
                });
            } else {
                alert(result.message || "Error saving booking");
            }
        } catch (err) {
            console.error(err);
            alert("Error connecting to server.");
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-cream py-6 md:py-12 px-4 font-outfit text-secondary relative overflow-hidden">
            {/* Background Decor */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>

            <div className="max-w-4xl mx-auto flex flex-col gap-6 relative z-10">

                {/* Compact Nav */}
                <div className="flex justify-between items-center px-2">
                    <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-primary font-bold hover:text-secondary transition-all text-sm group">
                        <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                        <span>Return</span>
                    </button>
                    <div className="flex items-center gap-2 px-4 py-1.5 bg-white rounded-2xl border border-primary/10 shadow-sm">
                        <Lock size={14} className="text-primary" />
                        <span className="text-[10px] font-black text-secondary/40 uppercase tracking-widest">Secure Checkout</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                    {/* LEFT: Summary & Contact */}
                    <div className="lg:col-span-5 space-y-6">
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-[2.5rem] p-8 shadow-xl shadow-primary/5 border border-primary/10 relative overflow-hidden">
                            <div className="absolute -right-6 -top-6 w-20 h-20 bg-primary/10 rounded-full blur-2xl" />
                            <div className="flex items-center gap-2 mb-6 relative z-10">
                                <ShoppingBag size={18} className="text-primary" />
                                <h3 className="font-black text-[10px] uppercase tracking-[0.2em] text-secondary/40">Reservation Summary</h3>
                            </div>
                            <div className="space-y-4 relative z-10">
                                <h2 className="text-2xl font-playfair font-black text-secondary leading-tight">{state.event.title}</h2>
                                <div className="flex flex-wrap gap-2">
                                    {state.selectedSeats.map(s => (
                                        <span key={s} className="px-3 py-1 bg-cream text-secondary font-bold rounded-xl text-[10px] border border-primary/10">{s}</span>
                                    ))}
                                </div>
                                <div className="pt-6 border-t border-dashed border-primary/20 flex justify-between items-end">
                                    <span className="text-[10px] font-black text-secondary/40 uppercase tracking-widest">Total Payable</span>
                                    <span className="text-3xl font-black text-primary italic">₹{finalAmount}</span>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div id="contact-form-section" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-[2.5rem] p-8 shadow-xl shadow-primary/5 border border-primary/10">
                            <div className="flex items-center gap-2 mb-6">
                                <div className="w-8 h-8 flex items-center justify-center bg-primary text-white rounded-xl font-black text-xs shadow-lg shadow-primary/20">1</div>
                                <h3 className="font-black text-[10px] uppercase tracking-[0.2em] text-secondary">Attendee Details</h3>
                            </div>
                            <div className="space-y-4">
                                <div className="relative">
                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-primary" size={16} />
                                    <input type="email" placeholder="Email Address" className={`w-full pl-12 pr-4 py-4 bg-cream/50 rounded-2xl border border-primary/5 outline-none font-bold text-sm focus:border-primary focus:bg-white transition-all ${touched.email && !emailRegex.test(email) ? 'border-red-400' : ''}`} value={email} onChange={(e) => setEmail(e.target.value)} onBlur={() => setTouched(prev => ({ ...prev, email: true }))} />
                                </div>
                                <div className="relative">
                                    <Smartphone className="absolute left-4 top-1/2 -translate-y-1/2 text-primary" size={16} />
                                    <input type="tel" placeholder="Mobile Number" className={`w-full pl-12 pr-4 py-4 bg-cream/50 rounded-2xl border border-primary/5 outline-none font-bold text-sm focus:border-primary focus:bg-white transition-all ${touched.phone && phone.length !== 10 ? 'border-red-400' : ''}`} value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))} onBlur={() => setTouched(prev => ({ ...prev, phone: true }))} maxLength="10" />
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* RIGHT: PAYMENT INTERFACE */}
                    <div className="lg:col-span-7">
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-[3rem] shadow-2xl border border-primary/10 overflow-hidden relative h-full flex flex-col">
                            <div className="bg-secondary p-6 text-white flex justify-between items-center shrink-0">
                                <div>
                                    <h2 className="text-lg font-playfair font-black italic tracking-tight uppercase leading-none">Step 2: Payment</h2>
                                    <p className="text-[9px] font-bold text-white/40 uppercase tracking-[0.2em] mt-2">Secure Divine Gateway</p>
                                </div>
                                <div className="flex bg-white/5 p-1 rounded-2xl border border-white/10 scale-90">
                                    <button onClick={() => setIsTestMode(false)} className={`px-5 py-2 rounded-xl text-[9px] font-black uppercase transition-all ${!isTestMode ? 'bg-primary text-white shadow-lg' : 'text-white/40 hover:text-white'}`}>Live</button>
                                    <button onClick={() => setIsTestMode(true)} className={`px-5 py-2 rounded-xl text-[9px] font-black uppercase transition-all ${isTestMode ? 'bg-primary text-white shadow-lg' : 'text-white/40 hover:text-white'}`}>Test</button>
                                </div>
                            </div>

                            <div className="p-8 md:p-10 flex-1 flex flex-col justify-between space-y-8 relative">
                                <AnimatePresence>
                                    {!isDetailsValid && (
                                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-20 bg-white/95 backdrop-blur-sm flex items-center justify-center p-10 text-center">
                                            <div className="space-y-4">
                                                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                                                    <Lock size={32} className="text-primary" />
                                                </div>
                                                <p className="font-black text-secondary/60 uppercase tracking-[0.3em] text-xs">Complete Details to Unlock Payment</p>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* AUTO FLOW: RAZORPAY */}
                                <div className="space-y-5">
                                    <div className="flex items-center gap-3 mb-2 px-2">
                                        <Sparkles size={16} className="text-primary" />
                                        <h4 className="text-[10px] font-black text-secondary/40 uppercase tracking-[0.2em]">Instant Confirmation</h4>
                                    </div>
                                    <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }} onClick={handleRazorpayPayment} className="w-full py-5 bg-primary text-white rounded-[1.5rem] font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-4 shadow-xl shadow-primary/20 group overflow-hidden relative">
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                                        {loading ? <Loader2 className="animate-spin" size={20} /> : <><CreditCard size={20} /> Pay via Gateway</>}
                                    </motion.button>
                                    <p className="text-center text-[9px] font-bold text-secondary/30 uppercase tracking-[0.2em] italic">Supports UPI, Cards, Netbanking & Wallets</p>
                                </div>

                                <div className="relative py-2"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-primary/10"></div></div><div className="relative flex justify-center"><span className="bg-white px-4 text-[9px] font-black text-secondary/30 uppercase tracking-widest">Or Scan Direct QR</span></div></div>

                                {/* MANUAL FLOW: QR + UTR */}
                                <div className="flex flex-col sm:flex-row gap-8 items-center bg-cream/50 p-6 rounded-[2rem] border border-primary/5">
                                    <motion.div whileTap={{ scale: 0.98 }} onClick={() => window.location.href = upiLink} className="bg-white p-4 rounded-3xl shadow-lg border border-primary/10 cursor-pointer relative group flex-shrink-0">
                                        <QRCode value={upiLink} size={110} className="w-[110px] h-[110px]" />
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 rounded-2xl sm:hidden font-black text-[10px] text-primary uppercase">Tap to Pay</div>
                                    </motion.div>

                                    <div className="flex-1 space-y-4 w-full">
                                        <p className="text-[10px] font-bold text-secondary/40 uppercase tracking-widest leading-relaxed">Enter the 12-digit UTR number from your payment receipt for manual verification.</p>
                                        <div className="flex flex-col gap-3">
                                            <input type="text" placeholder="UTR NUMBER (12 DIGITS)" className="w-full p-4 bg-white rounded-2xl border border-primary/10 font-black text-center text-sm tracking-[0.3em] outline-none focus:border-primary transition-all uppercase placeholder:tracking-normal placeholder:text-[10px]" value={utr} onChange={(e) => setUtr(e.target.value.replace(/[^0-9]/g, ''))} maxLength="12" />
                                            <button onClick={handleManualVerify} disabled={utr.length < 12} className="w-full py-3 bg-secondary text-white rounded-xl font-black text-[10px] uppercase tracking-[0.2em] disabled:opacity-30 hover:bg-primary transition-colors">Submit UTR</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>

                <p className="text-center text-[10px] font-black text-secondary/20 uppercase tracking-[0.5em] py-8 italic">Rotaract Bhavnagar Youth • A Legacy of Trust</p>
            </div>
        </div>
    );
};

export default PaymentScreen;
